import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import vehiculoService, { VehiculoReadDto } from '../../services/vehiculoService';
import PDFViewer from '../../components/shared/PDFViewer';

const Vehiculos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [vehiculos, setVehiculos] = useState<VehiculoReadDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState('Unidades');
  const [pdfLoading, setPdfLoading] = useState(false);

  const [openPrintPorNro, setOpenPrintPorNro] = useState(false);
  const [desdeNro, setDesdeNro] = useState('');
  const [hastaNro, setHastaNro] = useState('');
  const [printingPorNro, setPrintingPorNro] = useState(false);

  const goNuevo = useCallback(() => {
    navigate('/servicio/vehiculos/nuevo');
  }, [navigate]);

  const fetchVehiculos = useCallback(
    async (pageNum: number, pageSize: number, search: string) => {
      try {
        setLoading(true);
        const data = await vehiculoService.getVehiculos({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
        });
        setVehiculos(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        console.error('Error al cargar unidades:', error);
        enqueueSnackbar('Error al cargar unidades', { variant: 'error' });
        setVehiculos([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVehiculos(page, rowsPerPage, searchTerm);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fetchVehiculos]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Insert' && e.key !== 'F2') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'F2') return;
      }
      e.preventDefault();
      goNuevo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNuevo]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleDelete = async (patente: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta unidad?')) return;
    try {
      await vehiculoService.deleteVehiculo(patente);
      enqueueSnackbar('Unidad eliminada exitosamente', { variant: 'success' });
      fetchVehiculos(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      console.error('Error al eliminar unidad:', error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar unidad';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleEdit = (veh: VehiculoReadDto) => {
    const patente = (veh.patente || '').trim();
    if (patente) navigate(`/servicio/vehiculos/${encodeURIComponent(patente)}`);
  };

  const openPdfBlob = (blob: Blob, title: string) => {
    const url = window.URL.createObjectURL(blob);
    setPdfTitle(title);
    setPdfUrl(url);
  };

  const handleClosePdf = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  const handleImprimir = async () => {
    try {
      setPdfLoading(true);
      const blob = await vehiculoService.getPdf({
        searchTerm: searchTerm || undefined,
      });
      openPdfBlob(blob, 'Impresión de Unidades');
    } catch (error: unknown) {
      console.error('Error al generar PDF:', error);
      enqueueSnackbar('Error al generar el PDF de unidades', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleImprimirPorNro = async () => {
    if (!desdeNro.trim() || !hastaNro.trim()) {
      enqueueSnackbar('Ingrese Desde y Hasta', { variant: 'warning' });
      return;
    }
    try {
      setPrintingPorNro(true);
      const blob = await vehiculoService.getPdfPorNro(desdeNro, hastaNro);
      setOpenPrintPorNro(false);
      openPdfBlob(blob, `Impresión por Nro. ${desdeNro.trim()} - ${hastaNro.trim()}`);
    } catch (error: unknown) {
      console.error('Error al generar PDF por nro:', error);
      enqueueSnackbar('Error al generar el PDF por número', { variant: 'error' });
    } finally {
      setPrintingPorNro(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Unidades
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Tooltip title="Insert o F2">
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={goNuevo}>
                  Nueva Unidad
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={18} /> : <PrintIcon />}
                onClick={handleImprimir}
                disabled={pdfLoading}
              >
                Impresión
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => {
                  setDesdeNro('');
                  setHastaNro('');
                  setOpenPrintPorNro(true);
                }}
              >
                Impresión por Nro.
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por cliente, identificador o marca..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {vehiculos.map((veh) => {
                const patente = (veh.patente || '').trim();
                return (
                  <Card key={patente} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {patente}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {veh.nombreCliente || veh.nroCliente || '—'} · {veh.descripcion || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Marca: {veh.descripcionMarca || veh.codigoMarca || '—'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => handleEdit(veh)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(patente)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Identificador</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Marca</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehiculos.map((veh) => {
                    const patente = (veh.patente || '').trim();
                    return (
                      <TableRow key={patente} hover>
                        <TableCell>{patente}</TableCell>
                        <TableCell>{veh.nombreCliente || veh.nroCliente || '—'}</TableCell>
                        <TableCell>{veh.descripcion || '—'}</TableCell>
                        <TableCell>{veh.descripcionMarca || veh.codigoMarca || '—'}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleEdit(veh)} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDelete(patente)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={openPrintPorNro} onClose={() => !printingPorNro && setOpenPrintPorNro(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Impresión por Nro.</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Desde"
              value={desdeNro}
              onChange={(e) => setDesdeNro(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Hasta"
              value={hastaNro}
              onChange={(e) => setHastaNro(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrintPorNro(false)} disabled={printingPorNro}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleImprimirPorNro}
            disabled={printingPorNro}
            startIcon={printingPorNro ? <CircularProgress size={18} /> : <PrintIcon />}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePdf}
          title={pdfTitle}
          documentId="vehiculos"
          documentType="informeCliente"
          downloadFileName="vehiculos.pdf"
        />
      )}
    </Box>
  );
};

export default Vehiculos;
